import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CloudUpload as CloudUploadIcon,
  TableChart as TableChartIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  Domain as RoomIcon,
  Badge as TeacherIcon,
  School as CourseIcon,
  Person as StudentIcon,
  Groups as GroupIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import {
  importExportApi,
  EntitySchema,
  ImportResult,
} from '../services/importExportApi';

interface PreviewRow {
  index: number;
  data: Record<string, any>;
  isValid: boolean;
  errors: string[];
}

const getEntityIcon = (key: string) => {
  switch (key) {
    case 'rooms':
      return <RoomIcon sx={{ fontSize: 32, color: 'primary.main' }} />;
    case 'teachers':
      return <TeacherIcon sx={{ fontSize: 32, color: 'secondary.main' }} />;
    case 'students':
      return <StudentIcon sx={{ fontSize: 32, color: 'info.main' }} />;
    case 'groups':
      return <GroupIcon sx={{ fontSize: 32, color: 'warning.main' }} />;
    case 'courses':
      return <CourseIcon sx={{ fontSize: 32, color: 'success.main' }} />;
    default:
      return <TableChartIcon sx={{ fontSize: 32, color: 'primary.main' }} />;
  }
};

const ImportDataPage: React.FC = () => {
  const { t } = useLanguage();

  const [schemas, setSchemas] = useState<EntitySchema[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('rooms');
  const [loading, setLoading] = useState<boolean>(true);

  // Upload & parse state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updateExisting, setUpdateExisting] = useState<boolean>(true);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [filterMode, setFilterMode] = useState<'all' | 'valid' | 'invalid'>('all');
  const [schemaDetailsOpen, setSchemaDetailsOpen] = useState<boolean>(false);

  // Import execution state
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Charger les schémas
  const loadSchemas = async () => {
    try {
      setLoading(true);
      const data = await importExportApi.getSchemas();
      setSchemas(data);
      if (data.length > 0 && !data.some((s) => s.entityKey === selectedKey)) {
        setSelectedKey(data[0].entityKey);
      }
    } catch (err: any) {
      blobticMessage.error(err.message || 'Erreur lors du chargement des modèles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchemas();
  }, []);

  const currentSchema = schemas.find((s) => s.entityKey === selectedKey);

  // Fonctions d'internationalisation des entités et des champs
  const getEntityTitle = (key: string, fallback: string) => {
    const k = `academy.entity.${key}.title`;
    const translated = t(k);
    return translated !== k ? translated : fallback;
  };

  const getEntityDesc = (key: string, fallback: string) => {
    const k = `academy.entity.${key}.desc`;
    const translated = t(k);
    return translated !== k ? translated : fallback;
  };

  const getFieldLabel = (field: any) => {
    const key = field?.key || field?.fieldName;
    if (key) {
      const k = `academy.field.${key}.label`;
      const translated = t(k);
      if (translated !== k) return translated;
    }
    return field?.headerLabel || field?.label || field?.fieldName || field?.key || '';
  };

  const getFieldDesc = (field: any) => {
    const key = field?.key || field?.fieldName;
    if (key) {
      const k = `academy.field.${key}.desc`;
      const translated = t(k);
      if (translated !== k) return translated;
    }
    return field?.description || '';
  };

  const getDataTypeLabel = (type: string) => {
    const cleanType = String(type || '').toLowerCase();
    const k = `academy.dataType.${cleanType}`;
    const translated = t(k);
    return translated !== k ? translated : type;
  };

  // Changement d'entité : réinitialiser le fichier déposé
  const handleSelectEntity = (key: string) => {
    setSelectedKey(key);
    setSelectedFile(null);
    setPreviewRows([]);
    setImportResult(null);
  };

  // Téléchargement des modèles
  const handleDownloadExcel = () => {
    if (!currentSchema) return;
    importExportApi.downloadTemplateExcel(
      currentSchema,
      getEntityTitle(currentSchema.entityKey, currentSchema.displayName)
    );
  };

  const handleDownloadCsv = () => {
    if (!currentSchema) return;
    importExportApi.downloadTemplateCsv(currentSchema);
  };

  // Gestion du dépôt / upload de fichier
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentSchema) return;
    await processFile(file, currentSchema);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file || !currentSchema) return;
    await processFile(file, currentSchema);
  };

  const processFile = async (file: File, schema: EntitySchema) => {
    try {
      setSelectedFile(file);
      const { rows } = await importExportApi.parseUploadedFile(file, schema);

      if (rows.length === 0) {
        blobticMessage.warning(t('academy.import.msg_no_rows'));
        setPreviewRows([]);
        return;
      }

      // Valider les champs obligatoires pour chaque ligne
      const requiredFields = schema.fields.filter((f) => f.isRequired);
      const evaluated: PreviewRow[] = rows.map((r, idx) => {
        const rowErrors: string[] = [];
        requiredFields.forEach((rf) => {
          const val = r[rf.fieldName];
          if (val === undefined || val === null || String(val).trim() === '') {
            rowErrors.push(t('academy.import.required_missing', getFieldLabel(rf)));
          }
        });
        return {
          index: idx + 1,
          data: r,
          isValid: rowErrors.length === 0,
          errors: rowErrors,
        };
      });

      setPreviewRows(evaluated);
      blobticMessage.success(t('academy.import.msg_rows_detected', rows.length));
    } catch (err: any) {
      blobticMessage.error(err.message || 'Format invalide');
      setSelectedFile(null);
      setPreviewRows([]);
    }
  };

  // Lancement de l'importation
  const handleExecuteImport = async () => {
    if (!currentSchema || previewRows.length === 0) return;

    const validRows = previewRows.map((p) => p.data);

    try {
      setIsImporting(true);
      const res = await importExportApi.importData(
        currentSchema.entityKey,
        validRows,
        updateExisting
      );

      setImportResult(res);
      setResultDialogOpen(true);

      if (res.totalErrors === 0) {
        blobticMessage.success(
          t('academy.import.msg_success', res.totalImported, res.totalUpdated)
        );
      } else {
        blobticMessage.warning(
          t('academy.import.msg_warning', res.totalErrors)
        );
      }

      // Recharger les compteurs de schémas
      loadSchemas();
    } catch (err: any) {
      blobticMessage.error(err.message || "Erreur lors de l'importation");
    } finally {
      setIsImporting(false);
    }
  };

  const totalValid = previewRows.filter((p) => p.isValid).length;
  const totalInvalid = previewRows.filter((p) => !p.isValid).length;

  const displayedRows = previewRows.filter((p) => {
    if (filterMode === 'valid') return p.isValid;
    if (filterMode === 'invalid') return !p.isValid;
    return true;
  });

  return (
    <GenericPage
      title={t('academy.import.page_title')}
      icon={<UploadFileIcon />}
      footerEnabled={false}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadSchemas}
          disabled={loading}
        >
          {t('academy.import.refresh')}
        </Button>
      }
    >
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        {/* Étape 1 : Choix de l'entité */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 'bold',
              }}
            >
              1
            </Box>
            {t('academy.import.step1_title')}
          </Typography>

          <Grid container spacing={2}>
            {schemas.map((s) => {
              const isSelected = s.entityKey === selectedKey;
              return (
                <Grid item xs={12} sm={6} md={2.4} key={s.entityKey}>
                  <Card
                    onClick={() => handleSelectEntity(s.entityKey)}
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      boxShadow: isSelected ? 4 : 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                        borderColor: 'primary.light',
                      },
                      bgcolor: isSelected ? 'action.selected' : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        {getEntityIcon(s.entityKey)}
                        {s.totalCount !== undefined && (
                          <Chip
                            size="small"
                            label={`${s.totalCount} ${t('academy.import.in_database')}`}
                            color={s.totalCount > 0 ? 'default' : 'secondary'}
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {getEntityTitle(s.entityKey, s.displayName)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: 32 }}>
                        {getEntityDesc(s.entityKey, s.description)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {currentSchema && (
          <>
            {/* Étape 2 : Téléchargement du modèle vierge & Zone de dépôt */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Carte Modèle vierge */}
              <Grid item xs={12} md={5}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 14,
                          fontWeight: 'bold',
                        }}
                      >
                        2
                      </Box>
                      {t('academy.import.step2_title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('academy.import.step2_desc', getEntityTitle(currentSchema.entityKey, currentSchema.displayName))}
                    </Typography>

                    <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadExcel}
                        sx={{ textTransform: 'none', fontWeight: 600, flex: 1 }}
                      >
                        {t('academy.import.btn_excel')}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadCsv}
                        sx={{ textTransform: 'none', fontWeight: 600, flex: 1 }}
                      >
                        {t('academy.import.btn_csv')}
                      </Button>
                    </Stack>

                    <Button
                      size="small"
                      startIcon={<HelpIcon />}
                      onClick={() => setSchemaDetailsOpen(true)}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                      {t('academy.import.view_rules', currentSchema.fields.length)}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Carte Dépôt du fichier */}
              <Grid item xs={12} md={7}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'info.main',
                          color: 'info.contrastText',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 14,
                          fontWeight: 'bold',
                        }}
                      >
                        3
                      </Box>
                      {t('academy.import.step3_title')}
                    </Typography>

                    <Box
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        border: '2px dashed',
                        borderColor: selectedFile ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: selectedFile ? 'action.hover' : 'background.default',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                      />
                      <CloudUploadIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1 }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {selectedFile
                          ? t('academy.import.file_selected', selectedFile.name, (selectedFile.size / 1024).toFixed(1))
                          : t('academy.import.drop_title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.import.accepted_formats')}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={updateExisting}
                            onChange={(e) => setUpdateExisting(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={500}>
                            {t('academy.import.update_existing')}
                          </Typography>
                        }
                      />

                      {previewRows.length > 0 && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          startIcon={<PlayArrowIcon />}
                          onClick={handleExecuteImport}
                          disabled={isImporting || totalValid === 0}
                          sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}
                        >
                          {isImporting ? t('academy.import.importing') : t('academy.import.btn_import', totalValid)}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Étape 4 : Prévisualisation & Analyse des données */}
            {previewRows.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {t('academy.import.preview_title', previewRows.length)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.import.preview_desc')}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant={filterMode === 'all' ? 'contained' : 'outlined'}
                        onClick={() => setFilterMode('all')}
                        sx={{ textTransform: 'none' }}
                      >
                        {t('academy.import.filter_all', previewRows.length)}
                      </Button>
                      <Button
                        size="small"
                        color="success"
                        variant={filterMode === 'valid' ? 'contained' : 'outlined'}
                        onClick={() => setFilterMode('valid')}
                        startIcon={<CheckCircleIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        {t('academy.import.filter_valid', totalValid)}
                      </Button>
                      {totalInvalid > 0 && (
                        <Button
                          size="small"
                          color="error"
                          variant={filterMode === 'invalid' ? 'contained' : 'outlined'}
                          onClick={() => setFilterMode('invalid')}
                          startIcon={<ErrorIcon />}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('academy.import.filter_errors', totalInvalid)}
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  {isImporting && <LinearProgress sx={{ mb: 2 }} />}

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, width: 60 }}>{t('academy.import.col_index')}</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 120 }}>{t('academy.import.col_status')}</TableCell>
                          {currentSchema.fields.map((f) => (
                            <TableCell key={f.fieldName || f.key} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {getFieldLabel(f)} {f.isRequired && <span style={{ color: 'red' }}>*</span>}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedRows.slice(0, 100).map((row) => (
                          <TableRow
                            key={row.index}
                            sx={{
                              bgcolor: row.isValid ? 'inherit' : 'error.lighter',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <TableCell>{row.index}</TableCell>
                            <TableCell>
                              {row.isValid ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  label={t('academy.import.status_valid')}
                                  icon={<CheckCircleIcon />}
                                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                />
                              ) : (
                                <Tooltip title={row.errors.join(' ')}>
                                  <Chip
                                    size="small"
                                    color="error"
                                    label={t('academy.import.status_incomplete')}
                                    icon={<ErrorIcon />}
                                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            {currentSchema.fields.map((f) => (
                              <TableCell key={f.fieldName || f.key} sx={{ whiteSpace: 'nowrap' }}>
                                {String(row.data[f.fieldName] ?? row.data[f.key] ?? '')}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {displayedRows.length > 100 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('academy.import.preview_limit', displayedRows.length)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Dialogue : Détail des colonnes du schéma (SANS AUCUN NOM DE TABLE SQL) */}
        {currentSchema && (
          <Dialog
            open={schemaDetailsOpen}
            onClose={() => setSchemaDetailsOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 700 }}>
              {t('academy.import.dialog_rules_title', getEntityTitle(currentSchema.entityKey, currentSchema.displayName))}
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                  'academy.import.dialog_rules_subtitle',
                  getEntityTitle(currentSchema.entityKey, currentSchema.displayName),
                  currentSchema.fields[0] ? getFieldLabel(currentSchema.fields[0]) : 'Code'
                )}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{t('academy.import.col_file_header')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('academy.import.col_data_type')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('academy.import.col_required')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('academy.import.col_sample')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('academy.import.col_description')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentSchema.fields.map((f) => (
                      <TableRow key={f.fieldName || f.key}>
                        <TableCell sx={{ fontWeight: 600 }}>{getFieldLabel(f)}</TableCell>
                        <TableCell>{getDataTypeLabel(f.dataType)}</TableCell>
                        <TableCell>
                          {f.isRequired ? (
                            <Chip size="small" color="error" label={t('academy.import.required_yes')} />
                          ) : (
                            <Chip size="small" variant="outlined" label={t('academy.import.required_optional')} />
                          )}
                        </TableCell>
                        <TableCell>{String(f.sampleValue ?? '-')}</TableCell>
                        <TableCell>{getFieldDesc(f) || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSchemaDetailsOpen(false)}>{t('academy.import.dialog_close')}</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Dialogue : Résultat d'importation */}
        <Dialog
          open={resultDialogOpen}
          onClose={() => setResultDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            {importResult && importResult.totalErrors === 0 ? (
              <CheckCircleIcon color="success" />
            ) : (
              <WarningIcon color="warning" />
            )}
            {t('academy.import.report_title')}
          </DialogTitle>
          <DialogContent dividers>
            {importResult && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                      <Typography variant="caption" color="text.secondary">{t('academy.import.report_processed')}</Typography>
                      <Typography variant="h6" fontWeight={700}>{importResult.totalProcessed}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.lighter' }}>
                      <Typography variant="caption" color="success.dark">{t('academy.import.report_created')}</Typography>
                      <Typography variant="h6" fontWeight={700} color="success.main">{importResult.totalImported}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'info.lighter' }}>
                      <Typography variant="caption" color="info.dark">{t('academy.import.report_updated')}</Typography>
                      <Typography variant="h6" fontWeight={700} color="info.main">{importResult.totalUpdated}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'error.lighter' }}>
                      <Typography variant="caption" color="error.dark">{t('academy.import.report_errors')}</Typography>
                      <Typography variant="h6" fontWeight={700} color="error.main">{importResult.totalErrors}</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {importResult.errors && importResult.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color="error" sx={{ mb: 1 }}>
                      {t('academy.import.report_errors_detail')}
                    </Typography>
                    <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1.5 }}>
                      {importResult.errors.map((err, i) => (
                        <Typography key={i} variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
                          {t('academy.import.report_row_error', err.rowIndex, err.code, err.errorMessage)}
                        </Typography>
                      ))}
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setResultDialogOpen(false);
                setSelectedFile(null);
                setPreviewRows([]);
              }}
              variant="contained"
            >
              {t('academy.import.report_finish')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GenericPage>
  );
};

export default ImportDataPage;
